import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  useSubscribeEmailMutation,
  useUnsubscribeEmailMutation,
  useGetEmailSubscriptionStatusQuery,
} from '../../features/auth/authApiSlice'
import { selectCurrentUser } from '../../features/auth/authSlice'
import { Loader } from '../../shared/Loader'
import toast from 'react-hot-toast'

export const EmailSubscription: React.FC = () => {
  const user = useSelector(selectCurrentUser)
  const [isChecked, setIsChecked] = useState(false)
  const [email, setEmail] = useState<string>('')

  const {
    data: subscriptionStatus,
    isLoading: isLoadingStatus,
    refetch,
  } = useGetEmailSubscriptionStatusQuery()

  const [subscribeEmail, { isLoading: isSubscribing }] = useSubscribeEmailMutation()
  const [unsubscribeEmail, { isLoading: isUnsubscribing }] = useUnsubscribeEmailMutation()

  useEffect(() => {
    if (subscriptionStatus) {
      setIsChecked(subscriptionStatus.isSubscribed)
      setEmail(subscriptionStatus.email)
    } else if (user?.email) {
      setEmail(user.email)
    }
  }, [subscriptionStatus, user])

  const handleCheckboxChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked

    if (checked) {
      try {
        await subscribeEmail().unwrap()
        setIsChecked(true)
        toast.success('Successfully subscribed to marketing emails')
        refetch()
      } catch (error: any) {
        setIsChecked(false)
        toast.error(error?.data?.message || 'Failed to subscribe to emails')
      }
    } else {
      if (!email) {
        toast.error('Email address not found')
        return
      }

      try {
        await unsubscribeEmail({ email }).unwrap()
        setIsChecked(false)
        toast.success('Successfully unsubscribed from marketing emails')
        refetch()
      } catch (error: any) {
        setIsChecked(true)
        toast.error(error?.data?.message || 'Failed to unsubscribe from emails')
      }
    }
  }

  if (isLoadingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    )
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <Helmet>
        <title>Email Subscription | Dashboard</title>
        <meta name="description" content="Manage your email subscription preferences" />
      </Helmet>

      <div className="max-w-2xl mx-auto">
        <div className="bg-black rounded-xl shadow-lg p-8 border border-gray-700">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Email Subscription</h1>
            <p className="text-gray-300">Manage your marketing email preferences</p>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-white text-lg mb-6">
                Click to subscribe to marketing emails. You can unsubscribe anytime.
              </p>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="emailSubscription"
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  disabled={isSubscribing || isUnsubscribing}
                  className="mt-1 w-5 h-5 text-primary bg-gray-700 border-gray-600 rounded focus:ring-primary focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <label
                  htmlFor="emailSubscription"
                  className="text-white text-base cursor-pointer flex-1"
                >
                  Yes, I want to receive marketing emails
                </label>
              </div>

              {(isSubscribing || isUnsubscribing) && (
                <div className="mt-4 flex items-center gap-2 text-gray-400">
                  <Loader />
                  <span className="text-sm">
                    {isSubscribing ? 'Subscribing...' : 'Unsubscribing...'}
                  </span>
                </div>
              )}

              {isChecked && subscriptionStatus?.isSubscribed && (
                <div className="mt-4 bg-green-900/30 border border-green-600 rounded-lg p-4">
                  <p className="text-green-300 text-sm">
                    You're now subscribed to marketing emails. You can unsubscribe anytime.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

